<template>
    <div>
        <div :data-type="type" class="form-item-radio-input"
             :class="wrapperClass">
            <label :for="'form-global-' + fieldId"
                   v-if="label">
                <span v-if="required">* </span>{{ label }}
            </label>
            <div v-if="question.optionHtml === 'default'" :class="elementClass">
                <div v-for="(label, optionValue) in options" :key="optionValue" class="form-check">
                    <input
                        :id="'form-global-' + typeFieldId + '_' + fieldId + '_' + optionValue"
                        :class="[inputClass, { 'is-invalid': shouldShowError }]"
                        :name="name"
                        :type="type"
                        :value="optionValue"
                        :checked="isOptionSelected(optionValue)"
                        @change="handleRadioChange(optionValue)" />

                    <label :for="'form-global-' + typeFieldId + '_' + fieldId + '_' + optionValue" class="form-check-label">
                        {{ label }}
                    </label>

                    <!-- Campo de texto para "other" -->
                    <div v-if="optionValue === 'other' && selectedValue == 'other'" class="mt-2">
                        <input
                            type="text"
                            class="form-control question-input-text-other"
                            :placeholder="otherText || 'Especificar...'"
                            v-model="otherValue"
                            :id="'form-global-' + fieldId"
                            :name="'question_' + typeFieldId + '_' + fieldId + '_' + otherId + '_text_other'"
                            :disabled="!isOptionSelected('other')"
                            @input="handleOtherTextChange($event.target.value)" />
                    </div>
                </div>
            </div>
            <div class="btn-group d-flex"
                 :class="[question.optionOrder === 'column' || question.optionOrder === '0' ? 'flex-column' : 'flex-wrap']"
                 v-if="question.optionHtml === 'button'">
                <div v-for="(label, optionValue) in options" :key="optionValue" class="form-check">
                    <input
                        :id="'form-global-' + typeFieldId + '_' + fieldId + '_' + optionValue"
                        :class="[inputClass, { 'is-invalid': shouldShowError }]"
                        :name="name"
                        :type="type"
                        :value="optionValue"
                        :checked="isOptionSelected(optionValue)"
                        @change="handleRadioChange(optionValue)" />

                    <label class="btn btn-outline-primary btn-check-icon-outline label-options"
                           :for="'form-global-' + typeFieldId + '_' + fieldId + '_' + optionValue">
                        {{ label }}
                    </label>

                    <!-- Campo de texto para "other" -->
                    <div v-if="optionValue === 'other' && selectedValue == 'other'" class="mt-2">fieldId
                        <input
                            type="text"
                            class="form-control question-input-text-other"
                            :placeholder="otherText || 'Especificar...'"
                            v-model="otherValue"
                            :id="'form-global-' + fieldId"
                            :name="'question_' + typeFieldId + '_' + fieldId + '_' + otherId + '_text_other'"
                            :disabled="!isOptionSelected('other')"
                            @input="handleOtherTextChange($event.target.value)" />
                    </div>
                </div>
            </div>
        </div>
        <div v-if="shouldShowError" class="has-error-msg invalid-feedback d-block">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script setup>
import { computed, watch, ref, onMounted } from 'vue';
import { useField } from 'vee-validate';
import { useTranslation } from '@/composables/useTranslation';

// INITIALIZE STORE & COMPOSABLES
const { translate } = useTranslation();

// PROPS & EMITS
const props = defineProps({
    elementClass: {
        type: String,
        required: false,
        default: '',
    },
    errorText: {
        type: String,
        required: false,
    },
    fieldId: {
        type: Number,
        required: true,
    },
    inputClass: {
        type: String,
        required: false,
    },
    label: {
        type: String,
        required: false,
        default: '',
    },
    modelAnswer: {  // Asegúrate de que esta prop esté definida
        type: [String, Number],
        required: false,
        default: ''
    },
    name: {
        type: String,
        required: true,
    },
    options: {
        type: Object,
        required: true,
    },
    otherId: {
        type: Number,
        required: false,
        default: 0,
    },
    otherText: {
        type: String,
        required: false,
    },
    question: {
        type: Object,
        required: true,
    },
    required: {
        type: Number,
        required: false,
    },
    type: {
        type: String,
        required: true,
    },
    typeFieldId: {
        type: Number,
        required: true,
    },
    validation: {
        type: Object,
        required: false,
    },
    wrapperClass: {
        type: String,
        required: false,
        default: '',
    },
    hasSubmitted: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelAnswer', 'register-field', 'field-change', 'other-selected']);

// REACTIVE VARIABLES
const selectedValue = ref('');
const otherValue = ref('');
const previousOtherValue = ref(''); // Guardar el texto previo de "other"

// COMPUTED PROPERTIES
const validationRules = computed(() => {
    return (value) => {
        if (props.validation.required && (!value || value.toString().trim() === '')) {
            return props.errorText || translate('label.this_value_is_required');
        }

        return true;
    };
});

// Solo mostrar errores después del primer submit
const shouldShowError = computed(() => {
    return props.hasSubmitted && errorMessage.value;
});

// METHODS
const handleRadioChange = (optionValue) => {
    selectedValue.value = optionValue;

    if (optionValue === 'other') {
        // Restaurar el valor previo de "other" si existe
        otherValue.value = previousOtherValue.value;
        const finalValue = otherValue.value || '';
        value.value = finalValue;
        emit('update:modelAnswer', 'other');
        emit('field-change', props.name, 'other');

        // Emitir evento para datos de "other"
        emit('other-selected', {
            optionId: props.otherId,
            otherValue: finalValue,
            isSelected: true
        });
    } else {
        // Guardar el texto actual de "other" antes de cambiar
        if (otherValue.value && selectedValue.value === 'other') {
            previousOtherValue.value = otherValue.value;
        }

        // Limpiar el campo other visualmente pero mantener el valor guardado
        otherValue.value = '';
        value.value = optionValue;
        emit('update:modelAnswer', optionValue);
        emit('field-change', props.name, optionValue);

        // Emitir evento indicando que "other" ya no está seleccionado
        emit('other-selected', {
            optionId: props.otherId,
            otherValue: '',
            isSelected: false
        });
    }
};

// Manejar cambios en el campo "other"
const handleOtherTextChange = (newText) => {
    otherValue.value = newText;
    previousOtherValue.value = newText;

    // Si "other" está seleccionado, actualizar el valor
    if (selectedValue.value === 'other') {
        value.value = newText;
        emit('update:modelAnswer', 'other');
        emit('field-change', props.name, 'other');

        // Emitir evento actualizado para datos de "other"
        emit('other-selected', {
            optionId: props.otherId,
            otherValue: newText,
            isSelected: true
        });
    }
};

// Verificar si una opción está seleccionada
const isOptionSelected = (optionValue) => {
    return selectedValue.value === optionValue;
};

// Configurar useField con validación lazy
const { value, errorMessage, meta } = useField(props.name, validationRules, {
    initialValue: props.question.answer || '',
    validateOnValueUpdate: false,
    validateOnMount: false,
});

// LIFECYCLE HOOKS
onMounted(() => {
    const initialValue = props.question.answer || '';
    emit('register-field', props.name, initialValue);
});

// Sincronizar con el valor externo
watch(() => props.modelAnswer, (newValue) => {
    const stringValue = newValue ? newValue.toString() : '';

    // Verificar si el valor corresponde a una de las opciones predefinidas
    const isKnownOption = Object.keys(props.options).includes(stringValue);

    if (isKnownOption) {
        selectedValue.value = stringValue;
        value.value = stringValue;
    } else if (stringValue) {
        // Si no es una opción conocida, es texto de "other"
        selectedValue.value = 'other';
        otherValue.value = stringValue;
        previousOtherValue.value = stringValue;
        value.value = stringValue;
    } else {
        selectedValue.value = '';
        otherValue.value = '';
        previousOtherValue.value = '';
        value.value = '';
    }
}, { immediate: true });

watch(() => props.question.answerOther, (newValue) => {
    otherValue.value = newValue || '';
}, { immediate: true });

</script>
